import { Layout } from 'antd';
import PropTypes from 'prop-types';
import { Suspense, lazy, useEffect } from 'react';
import { connect } from 'react-redux';

import { gtagAnalytics } from 'qw-ui-lib-v2';
import { Route, Routes, useLocation } from 'react-router';
import { GA_TRACKING_ID } from '../common/config';
import { PageTitles } from '../common/pageTitles';
import { RoutePaths } from '../common/routePaths';
import bindDispatch from '../common/util/redux';
import ErrorAlert from '../components/common/ErrorAlert';
import Footer from '../components/common/Footer';
import SuccessAlert from '../components/common/SuccessAlert';
import { ReactComponent as LoaderIcon } from '../icons/loading.svg';
import GroupDetailsAdmin from '../pages/Groups/GroupDetailsAdmin';
import '../style/defaultLayout.less';
import { setLanguage } from '../locale';
import { localeMap } from '../common/enums';

const Movies = lazy(() => import('../pages/Movies'));
const AppliancePage = lazy(
  () => import('../pages/AppliancePage/AppliancePage'),
);
const RequestsPage = lazy(() => import('../pages/RequestsPage/RequestsPage'));
const ApplianceDetailsPage = lazy(
  () => import('../pages/AppliancePage/ApplianceDetailsPage'),
);
const ErrorPage = lazy(() => import('../pages/errorPage/errorPage'));
const UsersPage = lazy(() => import('../pages/UsersPage/UsersPage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage/ReportsPage'));

const AdminUsersPage = lazy(
  () => import('../pages/AdminUsersPage/AdminUsersPage'),
);
const AdminGroupsPage = lazy(
  () => import('../pages/AdminGroupsPage/AdminGroupsPage'),
);
const CreateGroup = lazy(() => import('../pages/CreateGroup/CreateGroup'));
const Groups = lazy(() => import('../pages/Groups/Groups'));
const GroupDetail = lazy(() => import('../pages/Groups/GroupDetail'));
const EditGroup = lazy(() => import('../pages/EditGroup/EditGroup'));
const FacilityDevices = lazy(
  () => import('../pages/Facilities/FacilityDevices'),
);
const HoldOverOverKdmPage = lazy(
  () => import('../pages/HoldOverKdmPage/HoldOverKdmPage'),
);
const RequestViewPage = lazy(
  () => import('../pages/RequestsPage/RequestViewPage'),
);
const WireLinkDashboard = lazy(() => import('../pages/WireLink'));
const GroupsLogPage = lazy(() => import('../pages/Groups/GroupsLog'));
const UsersLogPage = lazy(() => import('../pages/UsersPage/UsersLog'));
const FacilitySuiteLogs = lazy(
  () => import('../pages/Facilities/FacilitySuiteLogs'),
);

gtagAnalytics.initializeScript(GA_TRACKING_ID);
gtagAnalytics.configureRoutes(PageTitles);

/**
 * Route configuration array with components wrapped in gtagAnalytics.withPageTracker
 * Each route includes path, component, and page title for analytics
 */
const routeConfig = [
  {
    path: RoutePaths.HOME,
    element: gtagAnalytics.withPageTracker(Movies, {
      pageTitle: PageTitles[RoutePaths.HOME],
    }),
  },
  {
    path: RoutePaths.APPLIANCES,
    element: gtagAnalytics.withPageTracker(AppliancePage, {
      pageTitle: PageTitles[RoutePaths.APPLIANCES],
    }),
  },
  {
    path: RoutePaths.REQUESTS,
    element: gtagAnalytics.withPageTracker(RequestsPage, {
      pageTitle: PageTitles[RoutePaths.REQUESTS],
    }),
  },
  {
    path: RoutePaths.REQUEST_VIEW,
    element: gtagAnalytics.withPageTracker(RequestViewPage, {
      pageTitle: PageTitles[RoutePaths.REQUEST_VIEW],
    }),
  },
  {
    path: RoutePaths.APPLIANCE_DETAILS,
    element: gtagAnalytics.withPageTracker(ApplianceDetailsPage, {
      pageTitle: PageTitles[RoutePaths.APPLIANCE_DETAILS],
    }),
  },
  {
    path: RoutePaths.HOLDOVER_KDMS,
    element: gtagAnalytics.withPageTracker(HoldOverOverKdmPage, {
      pageTitle: PageTitles[RoutePaths.HOLDOVER_KDMS],
    }),
  },
  {
    path: RoutePaths.USERS,
    element: gtagAnalytics.withPageTracker(UsersPage, {
      pageTitle: PageTitles[RoutePaths.USERS],
    }),
  },
  {
    path: RoutePaths.USERS_LOGS,
    element: gtagAnalytics.withPageTracker(UsersLogPage, {
      pageTitle: PageTitles[RoutePaths.USERS_LOGS],
    }),
  },
  {
    path: RoutePaths.ADMIN_USERS,
    element: gtagAnalytics.withPageTracker(AdminUsersPage, {
      pageTitle: PageTitles[RoutePaths.ADMIN_USERS],
    }),
  },
  {
    path: RoutePaths.ADMIN_GROUPS,
    element: gtagAnalytics.withPageTracker(AdminGroupsPage, {
      pageTitle: PageTitles[RoutePaths.ADMIN_GROUPS],
    }),
  },
  {
    path: RoutePaths.GROUPS,
    element: gtagAnalytics.withPageTracker(Groups, {
      pageTitle: PageTitles[RoutePaths.GROUPS],
    }),
  },
  {
    path: RoutePaths.GROUP_LOGS,
    element: gtagAnalytics.withPageTracker(GroupsLogPage, {
      pageTitle: PageTitles[RoutePaths.GROUP_LOGS],
    }),
  },
  {
    path: RoutePaths.GROUP_DETAIL,
    element: gtagAnalytics.withPageTracker(GroupDetail, {
      pageTitle: PageTitles[RoutePaths.GROUP_DETAIL],
    }),
  },
  {
    path: RoutePaths.EDIT_GROUP,
    element: gtagAnalytics.withPageTracker(EditGroup, {
      pageTitle: PageTitles[RoutePaths.EDIT_GROUP],
    }),
  },
  {
    path: RoutePaths.GROUP_ADMIN,
    element: gtagAnalytics.withPageTracker(GroupDetailsAdmin, {
      pageTitle: PageTitles[RoutePaths.GROUP_ADMIN],
    }),
  },
  {
    path: RoutePaths.CREATE_GROUP,
    element: gtagAnalytics.withPageTracker(CreateGroup, {
      pageTitle: PageTitles[RoutePaths.CREATE_GROUP],
    }),
  },
  {
    path: RoutePaths.FACILITY_DEVICES,
    element: gtagAnalytics.withPageTracker(FacilityDevices, {
      pageTitle: PageTitles[RoutePaths.FACILITY_DEVICES],
    }),
  },
  {
    path: RoutePaths.FACILITY_SUITE_LOGS,
    element: gtagAnalytics.withPageTracker(FacilitySuiteLogs, {
      pageTitle: PageTitles[RoutePaths.FACILITY_SUITE_LOGS],
    }),
  },
  {
    path: RoutePaths.REPORTS,
    element: gtagAnalytics.withPageTracker(ReportsPage, {
      pageTitle: PageTitles[RoutePaths.REPORTS],
    }),
  },
  {
    path: RoutePaths.WIRELINKS,
    element: gtagAnalytics.withPageTracker(WireLinkDashboard, {
      pageTitle: PageTitles[RoutePaths.WIRELINKS],
    }),
  },
  {
    path: RoutePaths.ERROR,
    element: gtagAnalytics.withPageTracker(ErrorPage, {
      pageTitle: PageTitles[RoutePaths.ERROR],
    }),
  },
];

const DefaultLayout = ({
  user,
  app: { errors, successMessages },
  actions: { fetchUserInfo },
}) => {
  const location = useLocation(); // Get current route
  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    if (user.info) {
      gtagAnalytics.setUserProperties({
        user_id: user.info.user_id,
        company_id: user.info.company_id,
      });
      setLanguage(localeMap[user.info.locale] || user.info.locale);
    }
  }, [user.info]);

  const shouldShowFooter =
    !location.pathname.startsWith('/request-holdover-kdms/') &&
    !location.pathname.startsWith('/requests');

  return (
    <main className="layout-wrapper qw-relative ">
      {user.info ? (
        <Layout id="mainLayout" className="qw-p-none">
          <Suspense fallback={<LoaderIcon className="page-loader" />}>
            <Routes>
              {routeConfig.map(({ path, element: TrackedComponent }) => (
                <Route key={path} path={path} element={<TrackedComponent />} />
              ))}
            </Routes>
          </Suspense>
        </Layout>
      ) : (
        <LoaderIcon className="page-loader qw-absolute qw-left-0 qw-right-0 qw-top-0 qw-bottom-0" />
      )}
      {shouldShowFooter && <Footer />}
      <ErrorAlert errors={errors} />
      <SuccessAlert successMessages={successMessages} />
    </main>
  );
};

function mapStateToProps(state) {
  return {
    app: state.app,
    user: state.user,
  };
}

DefaultLayout.propTypes = {
  app: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, bindDispatch)(DefaultLayout);
